import { useCallback, useEffect, useRef, useState } from 'react';

import { legacyAppSelectedPair } from './momentsSelectedPairContract';

import type {
  AppointmentCancellationInitiator,
  AppointmentKind,
  AppointmentScope,
  OneOffBookingPattern,
  PlatformAdminMutationResult,
  PlatformAdminOperationStatus,
  Recurrence,
  SeatType,
} from './types';

import {
  isPlatformAdminTerminalHydrationForbidden,
  shouldReconcilePlatformAdminAppointmentWrite,
} from '@/store/api/appointmentApi';
import {
  useBookMomentsSelectedPairAppointmentMutation,
  useCancelMomentsSelectedPairAppointmentMutation,
  useLazyGetMomentsSelectedPairAppointmentQuery,
  useLazyGetMomentsSelectedPairOperationQuery,
  useRescheduleMomentsSelectedPairAppointmentMutation,
} from '@/store/api/momentsAppointmentApi';

interface AdminLifecycleCommandBase {
  coachId: string;
  idempotencyKey: string;
  kind: AppointmentKind;
  memberId: string;
  presentation: { memberEmail: string };
}

export type AdminLifecycleCommand =
  | (AdminLifecycleCommandBase & {
      command: 'book';
      payload: {
        bookingPattern?: OneOffBookingPattern;
        calendarConflictOverride?: boolean;
        recurrence: Recurrence | null;
        seatType: SeatType | null;
        startAt: string;
        viewerTimezone: string;
      };
    })
  | (AdminLifecycleCommandBase & {
      appointmentId: string;
      command: 'reschedule';
      payload: {
        busyConflictsAcknowledged: boolean;
        expectedSeriesMutationVersion: number | null;
        expectedVersion: number;
        nearTermAcknowledged: boolean;
        reviewDigest: string;
        seriesId: string | null;
        scope: AppointmentScope;
        startAt: string;
        viewerTimezone: string;
      };
    })
  | (AdminLifecycleCommandBase & {
      appointmentId: string;
      command: 'cancel';
      payload: {
        expectedSeriesMutationVersion: number | null;
        expectedVersion: number;
        initiator: AppointmentCancellationInitiator;
        nearTermChemistryConfirmed: boolean;
        opsNote: string;
        reason: string | null;
        scope: AppointmentScope;
      };
    });

interface AdminLifecycleRecordBase {
  command: AdminLifecycleCommand;
  generation: number;
  submittedAt: string;
  updatedAt: string;
  version: 1;
}

export type AdminLifecycleRecord =
  | (AdminLifecycleRecordBase & {
      operationId: null;
      phase: 'ambiguous' | 'submitting';
      rejection: null;
      result: null;
      retryAfterSeconds: null;
    })
  | (AdminLifecycleRecordBase & {
      operationId: string;
      phase: 'pending';
      rejection: null;
      result: PlatformAdminMutationResult;
      retryAfterSeconds: number | null;
    })
  | (AdminLifecycleRecordBase & {
      operationId: string;
      phase: 'needs_reconciliation';
      rejection: null;
      result: PlatformAdminOperationStatus;
      retryAfterSeconds: null;
    })
  | (AdminLifecycleRecordBase & {
      operationId: string | null;
      phase: 'confirmed';
      rejection: null;
      result: PlatformAdminMutationResult | PlatformAdminOperationStatus;
      retryAfterSeconds: null;
    })
  | (AdminLifecycleRecordBase & {
      operationId: string | null;
      phase: 'rejected';
      rejection: NonNullable<PlatformAdminMutationResult['rejection']>;
      result: PlatformAdminMutationResult | PlatformAdminOperationStatus;
      retryAfterSeconds: null;
    });

type AdminLifecycleTerminalRecord = Extract<
  AdminLifecycleRecord,
  { phase: 'confirmed' | 'rejected' }
>;

type AdminLifecycleOwnership =
  | ['book', string, string, AppointmentKind]
  | ['book', string, string, 'coaching', 'one_off']
  | ['mutation', string, string];

const ownership = (command: AdminLifecycleCommand): AdminLifecycleOwnership => {
  if (command.command !== 'book')
    return ['mutation', command.coachId, command.appointmentId];
  if (
    command.kind === 'coaching' &&
    command.payload.bookingPattern?.type === 'one_off'
  )
    return ['book', command.coachId, command.memberId, 'coaching', 'one_off'];
  return ['book', command.coachId, command.memberId, command.kind];
};

export const adminLifecycleOwnershipKey = (
  command: AdminLifecycleCommand,
): string => JSON.stringify(ownership(command));

const utf8Base64Url = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window
    .btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/[=]+$/u, '');
};

export const adminLifecycleRecordKey = (
  command: AdminLifecycleCommand,
): string =>
  utf8Base64Url(JSON.stringify([ownership(command), command.idempotencyKey]));

export interface AdminLifecycleStoredEnvelope {
  key: string;
  ownershipKey: string;
  record: AdminLifecycleRecord;
}

export interface AdminLifecycleTransactionalStorage {
  transaction<T>(
    operation: (current: AdminLifecycleStoredEnvelope | null) => {
      next?: AdminLifecycleStoredEnvelope | null;
      result: T;
    },
    ownershipKey: string,
  ): Promise<T>;
}

const ADMIN_LIFECYCLE_DATABASE =
  'mento-platform-admin-appointment-lifecycle-v1';
const ADMIN_LIFECYCLE_STORE = 'admin-lifecycle-records-v1';
const ADMIN_LIFECYCLE_OWNERSHIP_INDEX = 'ownershipKey';
const ADMIN_LIFECYCLE_BROADCAST_CHANNEL =
  'mento-platform-admin-appointment-lifecycle-sync-v1';
const MAX_POLL_BACKOFF_MS = 30_000;

const pendingPollOperationId = (record: AdminLifecycleRecord | null) =>
  record?.phase === 'pending' ? record.operationId : null;

const pendingPollRetryAfterSeconds = (record: AdminLifecycleRecord | null) =>
  record?.phase === 'pending' ? record.retryAfterSeconds : null;

const pendingPollDelayMs = (
  retryAfterSeconds: number | null,
  failureCount: number,
) =>
  Math.min(
    MAX_POLL_BACKOFF_MS,
    Math.max(1000, (retryAfterSeconds ?? 5) * 1000) * 2 ** failureCount,
  );

export const PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE =
  'Manual review required. This Appointment operation cannot be changed or retried.';
export const PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE =
  'Appointment details are unavailable for this admin. Another authorized admin can resume the pending operation.';
export const PLATFORM_ADMIN_RESCHEDULE_REVIEW_REQUIRED_MESSAGE =
  'This saved reschedule has no authoritative review. It cannot be retried; Ops must reconcile the original attempt before starting a reviewed reschedule.';

const hasRescheduleReview = (command: AdminLifecycleCommand): boolean =>
  command.command !== 'reschedule' ||
  ((command.payload.seriesId === null ||
    (typeof command.payload.seriesId === 'string' &&
      command.payload.seriesId.trim().length > 0)) &&
    typeof command.payload.reviewDigest === 'string' &&
    command.payload.reviewDigest.trim().length > 0 &&
    typeof command.payload.busyConflictsAcknowledged === 'boolean' &&
    typeof command.payload.nearTermAcknowledged === 'boolean');

const assertRescheduleReview = (command: AdminLifecycleCommand): void => {
  if (!hasRescheduleReview(command))
    throw new Error(PLATFORM_ADMIN_RESCHEDULE_REVIEW_REQUIRED_MESSAGE);
};

const openAdminLifecycleDatabase = (
  factory: IDBFactory,
): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = factory.open(ADMIN_LIFECYCLE_DATABASE, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains(ADMIN_LIFECYCLE_STORE)) return;
      const store = database.createObjectStore(ADMIN_LIFECYCLE_STORE, {
        keyPath: 'key',
      });
      store.createIndex(
        ADMIN_LIFECYCLE_OWNERSHIP_INDEX,
        ADMIN_LIFECYCLE_OWNERSHIP_INDEX,
        { unique: true },
      );
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });

const indexedDbTransactionalStorage = (
  factory: IDBFactory,
): AdminLifecycleTransactionalStorage => ({
  transaction: async <T>(
    operation: (current: AdminLifecycleStoredEnvelope | null) => {
      next?: AdminLifecycleStoredEnvelope | null;
      result: T;
    },
    ownershipKey: string,
  ): Promise<T> => {
    const database = await openAdminLifecycleDatabase(factory);
    try {
      return await new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(
          ADMIN_LIFECYCLE_STORE,
          'readwrite',
        );
        const store = transaction.objectStore(ADMIN_LIFECYCLE_STORE);
        const request = store
          .index(ADMIN_LIFECYCLE_OWNERSHIP_INDEX)
          .get(ownershipKey);
        let result!: T;
        request.onsuccess = () => {
          try {
            const transition = operation(
              (request.result as AdminLifecycleStoredEnvelope | undefined) ??
                null,
            );
            result = transition.result;
            if ('next' in transition) {
              if (transition.next === null) {
                if (request.result) store.delete(request.result.key);
              } else if (transition.next) {
                store.put(transition.next);
              }
            }
          } catch (error) {
            transaction.abort();
            reject(error);
          }
        };
        request.onerror = () => reject(request.error);
        transaction.onabort = () => reject(transaction.error);
        transaction.onerror = () => reject(transaction.error);
        transaction.oncomplete = () => resolve(result);
      });
    } finally {
      database.close();
    }
  },
});

const sameCommand = (
  left: AdminLifecycleCommand,
  right: AdminLifecycleCommand,
): boolean => JSON.stringify(left) === JSON.stringify(right);

const envelopeFor = (
  record: AdminLifecycleRecord,
): AdminLifecycleStoredEnvelope => ({
  key: adminLifecycleRecordKey(record.command),
  ownershipKey: adminLifecycleOwnershipKey(record.command),
  record,
});

type AdminLifecycleClaimResult = {
  kind: 'blocked' | 'claimed' | 'resumed';
  record: AdminLifecycleRecord;
};

export const createPlatformAdminLifecycleStore = (
  storage: AdminLifecycleTransactionalStorage = indexedDbTransactionalStorage(
    window.indexedDB,
  ),
) => ({
  claim: (record: AdminLifecycleRecord) => {
    if (record.phase !== 'submitting' || record.generation !== 1) {
      return Promise.reject(
        new Error('An admin lifecycle claim must start at generation 1'),
      );
    }
    const envelope = envelopeFor(record);
    return storage.transaction<AdminLifecycleClaimResult>((current) => {
      if (!current) {
        return { next: envelope, result: { kind: 'claimed' as const, record } };
      }
      if (
        current.key === envelope.key &&
        sameCommand(current.record.command, record.command)
      ) {
        return {
          result: { kind: 'resumed' as const, record: current.record },
        };
      }
      return { result: { kind: 'blocked' as const, record: current.record } };
    }, envelope.ownershipKey);
  },
  clear: (expected: AdminLifecycleRecord) => {
    if (expected.phase !== 'confirmed' && expected.phase !== 'rejected') {
      return Promise.resolve(false);
    }
    const envelope = envelopeFor(expected);
    return storage.transaction((current) => {
      const matches =
        current?.key === envelope.key &&
        current.record.generation === expected.generation;
      return matches ? { next: null, result: true } : { result: false };
    }, envelope.ownershipKey);
  },
  load: (command: AdminLifecycleCommand) =>
    storage.transaction(
      (current) => ({ result: current?.record ?? null }),
      adminLifecycleOwnershipKey(command),
    ),
  transition: (expected: AdminLifecycleRecord, next: AdminLifecycleRecord) => {
    const expectedEnvelope = envelopeFor(expected);
    const nextEnvelope = envelopeFor(next);
    if (
      expectedEnvelope.key !== nextEnvelope.key ||
      !sameCommand(expected.command, next.command) ||
      next.generation !== expected.generation + 1 ||
      next.submittedAt !== expected.submittedAt
    ) {
      return Promise.resolve(false);
    }
    return storage.transaction((current) => {
      const matches =
        current?.key === expectedEnvelope.key &&
        current.record.generation === expected.generation &&
        sameCommand(current.record.command, expected.command);
      return matches ? { next: nextEnvelope, result: true } : { result: false };
    }, expectedEnvelope.ownershipKey);
  },
  discard: (expected: AdminLifecycleRecord) => {
    const envelope = envelopeFor(expected);
    return storage.transaction((current) => {
      const matches =
        current?.key === envelope.key &&
        current.record.generation === expected.generation;
      return matches ? { next: null, result: true } : { result: false };
    }, envelope.ownershipKey);
  },
});

export type PlatformAdminLifecycleStore = ReturnType<
  typeof createPlatformAdminLifecycleStore
>;

export interface PlatformAdminLifecycleGuardOptions {
  disableAutomaticPolling?: boolean;
  enabled?: boolean;
  now?: () => string;
  store?: PlatformAdminLifecycleStore;
}

const nextRecord = <T extends AdminLifecycleRecord>(
  current: AdminLifecycleRecord,
  fields: Omit<T, keyof AdminLifecycleRecordBase | 'generation'>,
  now: string,
): T =>
  ({
    ...current,
    ...fields,
    generation: current.generation + 1,
    updatedAt: now,
  }) as T;

// usePendingOperationPolling owns the pending-operation poll loop.
//
// MEN-4306: two things here are deliberate. The first read happens immediately
// rather than after the advertised hint, because the admission has already told
// us the operation is pending and staging measured admin mutations finishing in
// 2.2-4.5s while the browser waited 30-69s. And the loop is driven by an
// explicit attempt counter rather than by the identity of the record object: a
// focus, BroadcastChannel, or IndexedDB refresh yields a fresh record for the
// same pending operation, which used to clear the timer and restart the wait
// from zero, sometimes indefinitely.
const usePendingOperationPolling = ({
  disabled,
  record,
  resume,
}: {
  disabled: boolean;
  record: AdminLifecycleRecord | null;
  resume: () => Promise<unknown>;
}) => {
  const [pollFailureCount, setPollFailureCount] = useState(0);
  const [pollAttempt, setPollAttempt] = useState(0);
  const operationId = pendingPollOperationId(record);
  const retryAfterSeconds = pendingPollRetryAfterSeconds(record);

  const reset = useCallback(() => {
    setPollAttempt(0);
    setPollFailureCount(0);
  }, []);

  useEffect(() => {
    reset();
  }, [operationId, reset]);

  useEffect(() => {
    if (disabled || operationId === null) return undefined;
    const delay =
      pollAttempt === 0
        ? 0
        : pendingPollDelayMs(retryAfterSeconds, pollFailureCount);
    const timeout = window.setTimeout(() => {
      // resume() no-ops while a submission is still in flight, so the attempt
      // is bumped in every case to re-arm the loop instead of stranding it.
      resume()
        .then(() => setPollFailureCount(0))
        .catch(() =>
          setPollFailureCount((failures) => Math.min(failures + 1, 5)),
        )
        .finally(() => setPollAttempt((attempt) => attempt + 1));
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [
    disabled,
    operationId,
    pollAttempt,
    pollFailureCount,
    resume,
    retryAfterSeconds,
  ]);

  return reset;
};

export const usePlatformAdminAppointmentLifecycleGuard = (
  command: AdminLifecycleCommand,
  options: PlatformAdminLifecycleGuardOptions = {},
) => {
  const storeRef = useRef<PlatformAdminLifecycleStore>();
  if (!storeRef.current) {
    storeRef.current = options.store ?? createPlatformAdminLifecycleStore();
  }
  const store = storeRef.current;
  const commandRef = useRef(command);
  commandRef.current = command;
  const ownershipKey = adminLifecycleOwnershipKey(command);
  const enabled = options.enabled ?? true;
  const nowRef = useRef(options.now ?? (() => new Date().toISOString()));
  const [record, setRecord] = useState<AdminLifecycleRecord | null>(null);
  const [resultState, setResultState] = useState<{
    command: AdminLifecycleCommand;
    ownershipKey: string;
    value: PlatformAdminMutationResult | PlatformAdminOperationStatus;
  } | null>(null);
  const [errorState, setErrorState] = useState<{
    ownershipKey: string;
    value: unknown;
  } | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const [terminalHydrationForbidden, setTerminalHydrationForbidden] =
    useState(false);
  const inFlight = useRef(false);
  const [book] = useBookMomentsSelectedPairAppointmentMutation();
  const [reschedule] = useRescheduleMomentsSelectedPairAppointmentMutation();
  const [cancel] = useCancelMomentsSelectedPairAppointmentMutation();
  const [getOperation] = useLazyGetMomentsSelectedPairOperationQuery();
  const [getDetail] = useLazyGetMomentsSelectedPairAppointmentQuery();
  const visibleRecord =
    record && adminLifecycleOwnershipKey(record.command) === ownershipKey
      ? record
      : null;
  const result =
    !terminalHydrationForbidden && resultState?.ownershipKey === ownershipKey
      ? resultState.value
      : null;
  const publishedCommand =
    resultState?.ownershipKey === ownershipKey ? resultState.command : null;
  const error =
    !terminalHydrationForbidden && errorState?.ownershipKey === ownershipKey
      ? errorState.value
      : null;
  const publishResult = useCallback(
    (
      owner: AdminLifecycleCommand,
      value: PlatformAdminMutationResult | PlatformAdminOperationStatus,
    ) =>
      setResultState({
        command: owner,
        ownershipKey: adminLifecycleOwnershipKey(owner),
        value,
      }),
    [],
  );
  const publishError = useCallback(
    (owner: AdminLifecycleCommand, value: unknown) =>
      setErrorState({
        ownershipKey: adminLifecycleOwnershipKey(owner),
        value,
      }),
    [],
  );
  const announceOwnershipChange = useCallback(
    (owner: AdminLifecycleCommand) =>
      broadcastChannelRef.current?.postMessage({
        ownershipKey: adminLifecycleOwnershipKey(owner),
      }),
    [],
  );

  const persistTransition = useCallback(
    async <T extends AdminLifecycleRecord>(
      current: AdminLifecycleRecord,
      fields: Omit<T, keyof AdminLifecycleRecordBase | 'generation'>,
    ): Promise<T> => {
      const next = nextRecord<T>(current, fields, nowRef.current());
      if (!(await store.transition(current, next))) {
        const authoritative = await store.load(current.command);
        setRecord(authoritative);
        throw new Error('Admin lifecycle generation changed');
      }
      setRecord(next);
      announceOwnershipChange(current.command);
      return next;
    },
    [announceOwnershipChange, store],
  );

  const recoverTerminalRecord = useCallback(
    async (terminal: AdminLifecycleTerminalRecord) => {
      if (terminal.phase === 'confirmed') {
        if (!(await store.clear(terminal))) {
          const authoritative = await store.load(terminal.command);
          setRecord(authoritative);
          if (authoritative) {
            throw new Error('Confirmed admin lifecycle ownership changed');
          }
        }
        setRecord(null);
        publishResult(terminal.command, terminal.result);
        announceOwnershipChange(terminal.command);
        return;
      }
      const authoritative = await store.load(terminal.command);
      setRecord(authoritative);
      if (
        authoritative?.phase === 'rejected' &&
        authoritative.generation === terminal.generation &&
        sameCommand(authoritative.command, terminal.command)
      ) {
        publishResult(authoritative.command, authoritative.result);
      }
    },
    [announceOwnershipChange, publishResult, store],
  );

  const verifyMutationPreimage = useCallback(
    async (lifecycleCommand: AdminLifecycleCommand) => {
      if (lifecycleCommand.command === 'book') return;
      const request = getDetail({
        ...legacyAppSelectedPair(
          lifecycleCommand.coachId,
          lifecycleCommand.memberId,
        ),
        appointmentId: lifecycleCommand.appointmentId,
      });
      try {
        const appointment = await request.unwrap();
        // `nearTermConfirmationRequired` is deliberately not revalidated here.
        // The server is the authority on whether a near-term acknowledgement is
        // required: Moments rejects the command when it genuinely needs one and
        // that rejection surfaces through the existing rejection path. The admin
        // surface no longer collects the acknowledgement, so a client-side guard
        // enforcing a rule the client can no longer satisfy could only produce a
        // dead-end in which Confirm dispatches no mutation at all.
        const cancellationInvalid =
          lifecycleCommand.command === 'cancel' &&
          (!appointment.actionCapabilities.cancel.allowed ||
            !appointment.actionCapabilities.cancel.scopes.includes(
              lifecycleCommand.payload.scope,
            ) ||
            (appointment.actionCapabilities.cancel.reasonRequired &&
              !lifecycleCommand.payload.reason?.trim()) ||
            (appointment.actionCapabilities.cancel.opsNoteRequired &&
              !lifecycleCommand.payload.opsNote.trim()));
        if (
          appointment.id !== lifecycleCommand.appointmentId ||
          appointment.kind !== lifecycleCommand.kind ||
          appointment.coach.id !== lifecycleCommand.coachId ||
          appointment.member.id !== lifecycleCommand.memberId ||
          appointment.version !== lifecycleCommand.payload.expectedVersion ||
          appointment.seriesMutationVersion !==
            (lifecycleCommand.payload.expectedSeriesMutationVersion ?? 0) ||
          (lifecycleCommand.command === 'reschedule' &&
            (appointment.seriesId !== lifecycleCommand.payload.seriesId ||
              !appointment.actionCapabilities.reschedule.allowed ||
              !appointment.actionCapabilities.reschedule.scopes.includes(
                lifecycleCommand.payload.scope,
              ))) ||
          cancellationInvalid
        ) {
          throw new Error(
            'Admin Appointment details changed; review the latest state',
          );
        }
      } finally {
        request.unsubscribe();
      }
    },
    [getDetail],
  );

  const finishTerminal = useCallback(
    async (
      current: AdminLifecycleRecord,
      terminal: PlatformAdminMutationResult | PlatformAdminOperationStatus,
    ) => {
      if (terminal.status === 'pending') return current;
      if (terminal.status === 'needs_reconciliation') {
        const held = await persistTransition<AdminLifecycleRecord>(current, {
          operationId:
            terminal.contractVersion === 'platform-admin-operation.v1'
              ? terminal.id
              : terminal.operationId,
          phase: 'needs_reconciliation',
          rejection: null,
          result: terminal,
          retryAfterSeconds: null,
        });
        publishResult(current.command, terminal);
        return held;
      }
      if (terminal.status === 'rejected' && !terminal.rejection) {
        throw new Error('Rejected admin lifecycle result has no rejection');
      }
      const terminalRecord =
        await persistTransition<AdminLifecycleTerminalRecord>(
          current,
          terminal.status === 'confirmed'
            ? {
                operationId:
                  terminal.contractVersion === 'platform-admin-operation.v1'
                    ? terminal.id
                    : terminal.operationId,
                phase: 'confirmed',
                rejection: null,
                result: terminal,
                retryAfterSeconds: null,
              }
            : {
                operationId:
                  terminal.contractVersion === 'platform-admin-operation.v1'
                    ? terminal.id
                    : terminal.operationId,
                phase: 'rejected',
                rejection: terminal.rejection!,
                result: terminal,
                retryAfterSeconds: null,
              },
        );
      await recoverTerminalRecord(terminalRecord);
      return terminalRecord;
    },
    [persistTransition, publishResult, recoverTerminalRecord],
  );

  const invokeMutation = useCallback(
    async (current: AdminLifecycleRecord) => {
      const lifecycleCommand = current.command;
      assertRescheduleReview(lifecycleCommand);
      let response: PlatformAdminMutationResult;
      const selectedPair = legacyAppSelectedPair(
        lifecycleCommand.coachId,
        lifecycleCommand.memberId,
      );
      try {
        if (lifecycleCommand.command === 'book') {
          response = await book({
            ...selectedPair,
            calendarConflictOverride:
              lifecycleCommand.payload.calendarConflictOverride ?? false,
            idempotencyKey: lifecycleCommand.idempotencyKey,
            kind: lifecycleCommand.kind,
            ...lifecycleCommand.payload,
          }).unwrap();
        } else if (lifecycleCommand.command === 'reschedule') {
          response = await reschedule({
            ...selectedPair,
            appointmentId: lifecycleCommand.appointmentId,
            idempotencyKey: lifecycleCommand.idempotencyKey,
            ...lifecycleCommand.payload,
          }).unwrap();
        } else {
          response = await cancel({
            ...selectedPair,
            appointmentId: lifecycleCommand.appointmentId,
            idempotencyKey: lifecycleCommand.idempotencyKey,
            ...lifecycleCommand.payload,
          }).unwrap();
        }
      } catch (cause) {
        publishError(current.command, cause);
        if (shouldReconcilePlatformAdminAppointmentWrite(cause)) {
          await persistTransition<AdminLifecycleRecord>(current, {
            operationId: null,
            phase: 'ambiguous',
            rejection: null,
            result: null,
            retryAfterSeconds: null,
          });
        } else if (current.phase !== 'ambiguous') {
          await store.discard(current);
          setRecord(null);
          announceOwnershipChange(current.command);
        } else {
          setRecord(current);
        }
        throw cause;
      }
      setErrorState(null);
      if (response.status === 'pending') {
        publishResult(current.command, response);
        await persistTransition<AdminLifecycleRecord>(current, {
          operationId: response.operationId,
          phase: 'pending',
          rejection: null,
          result: response,
          retryAfterSeconds: response.retryAfterSeconds,
        });
      } else {
        await finishTerminal(current, response);
      }
      return response;
    },
    [
      book,
      cancel,
      announceOwnershipChange,
      finishTerminal,
      persistTransition,
      publishError,
      publishResult,
      reschedule,
      store,
    ],
  );

  const resume = useCallback(async () => {
    if (inFlight.current) return null;
    inFlight.current = true;
    const requestedCommand = commandRef.current;
    try {
      let current = await store.load(requestedCommand);
      if (!current) {
        setRecord(null);
        return null;
      }
      setRecord(current);
      if (current.phase === 'submitting') {
        current = await persistTransition<AdminLifecycleRecord>(current, {
          operationId: null,
          phase: 'ambiguous',
          rejection: null,
          result: null,
          retryAfterSeconds: null,
        });
      }
      if (current.phase === 'ambiguous') return await invokeMutation(current);
      if (current.phase === 'needs_reconciliation') return null;
      if (current.phase === 'pending') {
        const request = getOperation({
          ...legacyAppSelectedPair(
            current.command.coachId,
            current.command.memberId,
          ),
          operationId: current.operationId,
        });
        try {
          const operation = await request.unwrap();
          if (operation.status === 'pending') {
            publishResult(current.command, operation);
            const pending = await persistTransition<AdminLifecycleRecord>(
              current,
              {
                operationId: current.operationId,
                phase: 'pending',
                rejection: null,
                result: current.result,
                retryAfterSeconds: operation.retryAfterSeconds,
              },
            );
            return pending.result;
          }
          await finishTerminal(current, operation);
          return operation;
        } catch (cause) {
          if (!isPlatformAdminTerminalHydrationForbidden(cause)) throw cause;

          setResultState((published) =>
            published?.ownershipKey ===
            adminLifecycleOwnershipKey(current.command)
              ? null
              : published,
          );
          setErrorState((published) =>
            published?.ownershipKey ===
            adminLifecycleOwnershipKey(current.command)
              ? null
              : published,
          );
          setTerminalHydrationForbidden(true);
          return null;
        } finally {
          request.unsubscribe();
        }
      }
      if (current.phase === 'confirmed') {
        await recoverTerminalRecord(current);
        return current.result;
      }
      if (current.phase === 'rejected') {
        await recoverTerminalRecord(current);
        return current.result;
      }
      return null;
    } catch (cause) {
      publishError(requestedCommand, cause);
      throw cause;
    } finally {
      inFlight.current = false;
    }
  }, [
    finishTerminal,
    getOperation,
    invokeMutation,
    persistTransition,
    publishError,
    publishResult,
    recoverTerminalRecord,
    store,
  ]);

  const submit = useCallback(async () => {
    if (inFlight.current) return null;
    inFlight.current = true;
    const requestedCommand = commandRef.current;
    try {
      const existing = await store.load(requestedCommand);
      if (existing) {
        setRecord(existing);
        if (existing.phase === 'ambiguous' || existing.phase === 'submitting') {
          let replay: AdminLifecycleRecord = existing;
          if (replay.phase === 'submitting') {
            replay = await persistTransition<AdminLifecycleRecord>(replay, {
              operationId: null,
              phase: 'ambiguous',
              rejection: null,
              result: null,
              retryAfterSeconds: null,
            });
          }
          return await invokeMutation(replay);
        }
        if (existing.phase === 'pending') return existing.result;
        if (existing.phase === 'needs_reconciliation') {
          if (!sameCommand(existing.command, requestedCommand)) {
            throw new Error(
              'Admin operation requires reconciliation before a new command',
            );
          }
          publishResult(existing.command, existing.result);
          return null;
        }
        const terminalResult = existing.result;
        if (!terminalResult) return null;
        await recoverTerminalRecord(existing);
        return terminalResult.contractVersion ===
          'platform-admin-mutation-result.v1'
          ? terminalResult
          : null;
      }
      assertRescheduleReview(requestedCommand);
      await verifyMutationPreimage(requestedCommand);
      const submittedAt = nowRef.current();
      const initial: AdminLifecycleRecord = {
        command: requestedCommand,
        generation: 1,
        operationId: null,
        phase: 'submitting',
        rejection: null,
        result: null,
        retryAfterSeconds: null,
        submittedAt,
        updatedAt: submittedAt,
        version: 1,
      };
      const claim = await store.claim(initial);
      setRecord(claim.record);
      if (claim.kind === 'claimed')
        announceOwnershipChange(claim.record.command);
      if (claim.kind === 'blocked') {
        throw new Error('Another admin lifecycle command owns this target');
      }
      if (claim.record.phase === 'pending') return claim.record.result;
      if (claim.record.phase === 'rejected') {
        await recoverTerminalRecord(claim.record);
        return claim.record.result.contractVersion ===
          'platform-admin-mutation-result.v1'
          ? claim.record.result
          : null;
      }
      return await invokeMutation(claim.record);
    } catch (cause) {
      publishError(requestedCommand, cause);
      throw cause;
    } finally {
      inFlight.current = false;
    }
  }, [
    invokeMutation,
    persistTransition,
    announceOwnershipChange,
    publishError,
    recoverTerminalRecord,
    store,
    verifyMutationPreimage,
  ]);

  const clear = useCallback(async () => {
    const current = await store.load(commandRef.current);
    if (!current || !(await store.clear(current))) return false;
    setRecord(null);
    setResultState(null);
    setErrorState(null);
    announceOwnershipChange(current.command);
    return true;
  }, [announceOwnershipChange, store]);

  const consumeResult = useCallback(() => {
    setResultState((current) =>
      current?.ownershipKey === adminLifecycleOwnershipKey(commandRef.current)
        ? null
        : current,
    );
  }, []);

  const refreshFromStore = useCallback(async () => {
    const loadedCommand = commandRef.current;
    let stored = await store.load(loadedCommand);
    if (stored?.phase === 'submitting') {
      stored = await persistTransition<AdminLifecycleRecord>(stored, {
        operationId: null,
        phase: 'ambiguous',
        rejection: null,
        result: null,
        retryAfterSeconds: null,
      });
    }
    setRecord(stored);
    if (stored?.phase === 'pending') {
      publishResult(stored.command, stored.result);
    }
    if (stored?.phase === 'needs_reconciliation') {
      publishResult(stored.command, stored.result);
    }
    if (stored?.phase === 'confirmed' || stored?.phase === 'rejected') {
      await recoverTerminalRecord(stored);
    }
  }, [persistTransition, publishResult, recoverTerminalRecord, store]);

  const resetPolling = usePendingOperationPolling({
    disabled:
      Boolean(options.disableAutomaticPolling) || terminalHydrationForbidden,
    record: visibleRecord,
    resume,
  });

  useEffect(() => {
    resetPolling();
    setTerminalHydrationForbidden(false);
    if (!enabled) {
      setRecord(null);
      return undefined;
    }
    let active = true;
    const refresh = () =>
      refreshFromStore().catch((cause) => {
        if (active) publishError(commandRef.current, cause);
      });
    const handleFocus = () => refresh();
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(ADMIN_LIFECYCLE_BROADCAST_CHANNEL);
      broadcastChannelRef.current = channel;
      channel.onmessage = (event: MessageEvent<unknown>) => {
        const { data } = event;
        if (
          data &&
          typeof data === 'object' &&
          'ownershipKey' in data &&
          data.ownershipKey === ownershipKey
        ) {
          refresh();
        }
      };
    }
    window.addEventListener('focus', handleFocus);
    refresh();
    return () => {
      active = false;
      window.removeEventListener('focus', handleFocus);
      channel?.close();
      if (broadcastChannelRef.current === channel) {
        broadcastChannelRef.current = null;
      }
    };
  }, [enabled, ownershipKey, publishError, refreshFromStore, resetPolling]);

  return {
    clear,
    command: publishedCommand ?? visibleRecord?.command ?? command,
    consumeResult,
    error,
    record: visibleRecord,
    requiresRescheduleReview:
      visibleRecord !== null &&
      (visibleRecord.phase === 'ambiguous' ||
        visibleRecord.phase === 'submitting') &&
      !hasRescheduleReview(visibleRecord.command),
    result,
    resume,
    submit,
    terminalHydrationForbidden,
  };
};
